import { cn } from '../lib/cn.js';
import markUrl from './assets/kainos-mark-256.png';
import logoUrl from './assets/kainos-logo-96.png';

export interface MarkProps {
  size?: number;
  className?: string;
}

/**
 * The official KainOS monogram: a navy `K` interlocked with a blue `O`.
 *
 * Rendered from the master artwork rather than a hand-traced SVG, so the shape
 * is exactly the approved one. The source is 256px, which covers every place it
 * is used — 20–40px in the app chrome — at 3x on a retina display.
 */
export function BrandMark({ size = 32, className }: MarkProps) {
  return (
    <img
      src={markUrl}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      className={cn('inline-block shrink-0 select-none object-contain', className)}
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

/** The blue AI tile that prefixes every KainOS AI attribution. */
export function AiMark({ size = 20, className }: MarkProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md bg-primary',
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none">
        <rect x="4" y="4" width="16" height="16" rx="3" stroke="#fff" strokeWidth="1.8" />
        <rect x="9.5" y="9.5" width="5" height="5" rx="1" fill="#fff" />
      </svg>
    </span>
  );
}

export interface BrandLockupProps {
  className?: string;
  markSize?: number;
}

/**
 * Monogram plus wordmark, used in the sidebar header.
 *
 * The wordmark is set in type rather than pulled from the logo image: it stays
 * crisp at any size, inherits the app's font, and reproduces the official
 * two-tone split — `Kain` in the logo navy, `OS` in the logo blue.
 */
export function BrandLockup({ className, markSize = 30 }: BrandLockupProps) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <BrandMark size={markSize} />
      <span className="text-[18px] font-extrabold leading-none tracking-[-0.03em]">
        <span className="text-logo-navy">Kain</span>
        <span className="text-logo-blue">OS</span>
      </span>
    </div>
  );
}

export interface BrandLogoProps {
  /** Rendered height in px; the width follows the artwork's ratio. */
  height?: number;
  className?: string;
}

/**
 * The full official logo, including the "AI Project Operating System" tagline.
 *
 * Meant for surfaces with room to breathe — sign-in, exported reports, an empty
 * state. The sidebar uses `BrandLockup`, where the tagline would be unreadable.
 */
export function BrandLogo({ height = 48, className }: BrandLogoProps) {
  return (
    <img
      src={logoUrl}
      alt="KainOS — AI Project Operating System"
      height={height}
      className={cn('select-none object-contain', className)}
      style={{ height }}
      draggable={false}
    />
  );
}
